import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageContext } from '@/i18n/LanguageContext';
import { courseBenefits } from '@/mocks/benefitsData';

const courseImages: Record<string, string> = {
  essential: '/images/generated/benefits-essential-2026.jpg',
  advanced: '/images/generated/benefits-advanced-2026.jpg',
  master: '/images/generated/benefits-master-2026.jpg',
};

export default function CourseBenefits() {
  const { t } = useTranslation();
  const { lang } = useLanguageContext();
  return (
    <section className="relative py-20 md:py-28 bg-background-50">
      <div className="max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16">
        <div className="text-center mb-14">
          {String(t('benefits.course.label')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">{__s}</p>))}
          <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
            {t('benefits.course.title')}
          </h2>
          {String(t('benefits.course.titleEn')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-accent-500 text-[15px] tracking-[0.12em] mb-4 font-label">{__s}</p>))}
          <div className="w-10 h-[1px] bg-accent-500/40 mx-auto mb-6"></div>
          {String(t('benefits.course.lead')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[15px] md:text-[16px] text-foreground-500 tracking-[0.04em] leading-relaxed max-w-[700px] mx-auto">{__s}</p>))}
        </div>

        <div className="space-y-12">
          {courseBenefits.map((course, courseIdx) => (
            <div
              key={course.id}
              className={`relative overflow-hidden ${
                courseIdx % 2 === 1
                  ? 'bg-background-100/70'
                  : 'bg-background-50'
              }`}
            >
              <div className="flex flex-col lg:flex-row">
                <div className={`w-full lg:w-[380px] flex-shrink-0 ${courseIdx % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={courseImages[course.id]}
                      alt={lang === 'en' ? t(`benefits.courseBenefits.${courseIdx}.imageAlt`) : course.imageAlt}
                      className="absolute inset-0 w-full h-full object-cover object-top"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                      <p className="text-[13px] text-foreground-50/90 tracking-[0.05em]">
                        {lang === 'en' ? t(`benefits.courseBenefits.${courseIdx}.imageCaption`) : course.imageCaption}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex-1 p-6 md:p-8 lg:p-10">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-accent-500 text-[13px] tracking-[0.15em] font-label">
                      {String(courseIdx + 1).padStart(2, '0')}
                    </span>
                    <h3 className="font-heading text-lg md:text-xl text-foreground-900 tracking-[0.06em]">
                      {lang === 'en' ? t(`benefits.courseBenefits.${courseIdx}.name`) : course.name}
                    </h3>
                  </div>
                  <p className="text-accent-500 text-[13px] tracking-[0.12em] mb-3 font-label">
                    {course.nameEn}
                  </p>
                  <p className="text-[15px] text-foreground-500 tracking-[0.04em] leading-relaxed mb-6">
                    {lang === 'en' ? t(`benefits.courseBenefits.${courseIdx}.lead`) : course.lead}
                  </p>

                  <ul className="space-y-3">
                    {course.items.map((item, itemIdx) => (
                      <li key={itemIdx} className="flex items-start gap-3">
                        <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 mt-[1px] ${
                          item.inherited
                            ? 'text-foreground-400'
                            : 'text-accent-500'
                        }`}>
                          <i className={`${item.icon} text-sm`}></i>
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className={`text-[15px] tracking-[0.04em] font-medium ${
                              item.inherited ? 'text-foreground-600' : 'text-foreground-800'
                            }`}>
                              {lang === 'en' ? t(`benefits.courseBenefits.${courseIdx}.item.${itemIdx}.name`) : item.name}
                            </span>
                            {item.inherited && (
                              <span className="text-[12px] text-foreground-400 tracking-[0.06em] bg-background-200/60 px-2 py-[1px] rounded-full">
                                {t('benefits.course.inheritedBadge')}
                              </span>
                            )}
                          </div>
                          <p className="text-[13px] text-foreground-400 tracking-[0.03em] leading-relaxed mt-[2px]">
                            {lang === 'en' ? t(`benefits.courseBenefits.${courseIdx}.item.${itemIdx}.desc`) : item.description}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-6 pt-5 border-t border-background-200/60">
                    <Link
                      to={`/courses/${course.id}`}
                      className="inline-flex items-center gap-2 text-[14px] text-accent-600 hover:text-accent-500 tracking-[0.06em] transition-colors"
                    >
                      {lang === 'en' ? t(`benefits.course.courseLink.${courseIdx}`) : `${course.name}${t('benefits.course.courseLink')}`}
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-right-line text-xs"></i>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
